import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tours } from '@/mocks/tours';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { motion, AnimatePresence } from "framer-motion";
import TrustSection from '@/components/feature/TrustSection';
import ImportantInfo from '@/components/feature/ImportantInfo';

export default function TourDetail() {
    const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tour = tours.find((tr) => tr.id === id);
const [, setCurrencyVersion] = useState(0);

useEffect(() => {
  const handleCurrencyChange = () => {
    setCurrencyVersion((prev) => prev + 1);
  };

  window.addEventListener('currencyChange', handleCurrencyChange);

  return () => {
    window.removeEventListener('currencyChange', handleCurrencyChange);
  };
}, []);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const [bookingForm, setBookingForm] = useState({
    date: '',
    adults: 2,
    children: 0,
    hotel: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  const [formSent, setFormSent] = useState(false);
  const [submitUrl, setSubmitUrl] = useState('');

  if (!tour) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-28">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4 block" />
            <h2 className="font-heading text-2xl text-foreground-800 mb-2">
              Ausflug nicht gefunden
            </h2>
            <button onClick={() => navigate('/')} className="text-accent-600 hover:text-accent-500 text-sm cursor-pointer">
              Zurück zur Startseite
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const getCurrencySymbol = () => ((window as any).__CURRENCY__?.symbol) || '€';
  const getConvertedPrice = (p: number) => Math.round(p * (((window as any).__CURRENCY__?.rate) || 1));

  const allImages = [tour.image_url, ...tour.gallery].filter(Boolean);

  const getMinPrice = () => {
    if (tour.pricing_tiers && tour.pricing_tiers.length > 0) {
      const min = Math.min(...tour.pricing_tiers.map((t) => t.price));
      return getConvertedPrice(min);
    }
    return getConvertedPrice(tour.price);
  };

 const handleBookingSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  try {
    const response = await fetch('/api/send-email.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
        subject: `Yallabina Booking: ${tour.title_de}`,
        message: `
New Booking Request

Tour: ${tour.title_de}
Date: ${bookingForm.date}
Adults: ${bookingForm.adults}
Children: ${bookingForm.children}
Hotel: ${bookingForm.hotel}

Customer Name: ${bookingForm.name}
Email: ${bookingForm.email}
Phone: ${bookingForm.phone}

Notes:
${bookingForm.notes}
        `.trim(),
      }).toString(),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send booking request');
    }

    setFormSent(true);
  } catch (error) {
    console.error('Booking form error:', error);
    alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
  }
};

  const totalPrice = (() => {
    const adults = bookingForm.adults;
    const children = bookingForm.children;
    let adultPrice = tour.price;
    if (tour.pricing_tiers && tour.pricing_tiers.length > 0) {
      if (adults >= 7) {
        const tier = tour.pricing_tiers.find((t) => t.persons.includes('7')) || tour.pricing_tiers[tour.pricing_tiers.length - 1];
        adultPrice = tier.price;
      } else if (adults >= 5) {
        const tier = tour.pricing_tiers.find((t) => t.persons.includes('5')) || tour.pricing_tiers[0];
        adultPrice = tier.price;
      } else if (adults === 4) {
        const tier = tour.pricing_tiers.find((t) => t.persons.includes('4')) || tour.pricing_tiers[0];
        adultPrice = tier.price;
      } else if (adults === 3) {
        const tier = tour.pricing_tiers.find((t) => t.persons.includes('3')) || tour.pricing_tiers[0];
        adultPrice = tier.price;
      } else {
        adultPrice = tour.pricing_tiers[0].price;
      }
    }
    const childPrice = tour.child_price || (adultPrice / 2);
    return getConvertedPrice(adultPrice) * adults + getConvertedPrice(childPrice) * children;
  })();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 md:pt-32">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20 py-4">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <button onClick={() => navigate('/')} className="hover:text-accent-500 cursor-pointer">Yallabina</button>
            <i className="ri-arrow-right-s-line" />
            <button
              onClick={() => navigate(tour.departure_city === 'hurghada' ? '/ausfluege-hurghada' : '/ausfluege-marsa-alam')}
              className="hover:text-accent-500 cursor-pointer"
            >
              {tour.departure_city === 'hurghada' ? 'Hurghada' : 'Marsa Alam'}
            </button>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">
  {isDe ? tour.title_de : tour.title_en}
</span>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20 pb-20">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Gallery */}
              <div className="mb-6">
                <div className="relative rounded-sm overflow-hidden mb-3">

  <AnimatePresence mode="wait">

    <motion.img
  key={activeImage}
  src={allImages[activeImage] || tour.image_url}
  alt={isDe ? tour.title_de : tour.title_en}
  onClick={() => setLightboxOpen(true)}
  className="w-full h-auto object-contain cursor-zoom-in"
  initial={{ opacity: 0, scale: 1.04 }}
  animate={{ opacity: 1, scale: 1 }}
  exit={{ opacity: 0, scale: 0.98 }}
  transition={{ duration: 0.35 }}
/>

  </AnimatePresence>

</div>
                {allImages.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {allImages.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`flex-shrink-0 w-20 h-16 rounded-sm overflow-hidden border-2 transition-colors cursor-pointer ${
                          i === activeImage ? 'border-accent-500' : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-5">
                {isDe ? tour.title_de : tour.title_en}
              </h1>

              {/* Professional Info Bar */}
              <div className="bg-primary-800 rounded-sm p-4 md:p-5 mb-10">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {/* Rating */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-star-fill text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-[10px] text-background-400 uppercase tracking-wide">{isDe ? 'Bewertung' : 'Rating'}</span>
                      <span className="text-sm font-bold text-background-50">{tour.rating} <span className="text-xs text-background-400 font-normal">({tour.reviews_count})</span></span>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-[10px] text-background-400 uppercase tracking-wide">  {isDe ? 'Dauer' : 'Duration'}</span>
                      <span className="text-sm font-bold text-background-50">{tour.duration}</span>
                    </div>
                  </div>

                  {/* Hotel Pickup */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-car-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-[10px] text-background-400 uppercase tracking-wide">
  {isDe ? 'Hotelabholung inklusive' : 'Hotel Pickup Included'}
</span>
                      <span className="text-sm font-bold text-accent-400">{isDe ? 'Ja, inklusive' : 'Yes, included'}</span>
                    </div>
                  </div>

                  {/* German Guide */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-voice-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-[10px] text-background-400 uppercase tracking-wide">
  {isDe ? 'Deutschsprachiger Guide' : 'English-speaking Guide'}
</span>
                      <span className="text-sm font-bold text-accent-400">
  {isDe ? 'Ja' : 'Yes'}
</span>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex items-center gap-2.5 col-span-2 md:col-span-1">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-money-euro-circle-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-[10px] text-background-400 uppercase tracking-wide">{isDe ? 'Preis ab' : 'From'}</span>
                      <span className="text-sm font-bold text-background-50">
                        {getCurrencySymbol()}{getMinPrice()} <span className="text-xs text-background-400 font-normal">{isDe ? 'pro Person' : 'per person'}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Overview */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
  {isDe ? 'Überblick' : 'Overview'}
</h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  {isDe ? tour.description_de : tour.description_en}
                </p>
              </section>

              {/* Highlights */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">Highlights</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {(isDe ? tour.highlights_de : tour.highlights_en).map((h, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-accent-500 text-base" />
                      </div>
                      <span className="text-sm text-foreground-700 leading-relaxed">{h}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Itinerary */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
  {isDe ? 'Reiseverlauf' : 'Itinerary'}
</h2>
                <div className="relative pl-8 border-l-2 border-accent-200 space-y-6">
                 {(isDe ? tour.itinerary_de : tour.itinerary_en).map((step, i) => (
                    <div key={i} className="relative">
                      <div className="absolute -left-[37px] top-0.5 w-4 h-4 bg-accent-500 rounded-full border-2 border-background-50" />
                      <p className="text-sm text-foreground-700 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Included / Excluded */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <section>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
  {isDe ? 'Im Preis enthalten' : 'Included in the Price'}
</h2>
                  <div className="bg-background-100 rounded-sm p-6 space-y-3">
                    {(isDe ? tour.included_de : tour.included_en).map((inc, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-green-600 text-base" />
                        </div>
                        <span className="text-sm text-foreground-700">{inc}</span>
                      </div>
                    ))}
                  </div>
                </section>
                <section>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5"> {isDe ? 'Nicht enthalten' : 'Not Included'}</h2>
                  <div className="bg-background-100 rounded-sm p-6 space-y-3">
                    {(isDe ? tour.excluded_de : tour.excluded_en).map((exc, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-close-circle-fill text-foreground-400 text-base" />
                        </div>
                        <span className="text-sm text-foreground-600">{exc}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              {/* Trust Section */}
              <TrustSection />

              {/* FAQ */}
              {tour.faq.length > 0 && (
                <section className="mb-12 mt-14">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5"> {isDe ? 'Häufige Fragen' : 'Frequently Asked Questions'}</h2>
                  <div className="space-y-3">
                    {tour.faq.map((faq, i) => (
                      <div key={i} className="border border-background-200 rounded-sm overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50 transition-colors"
                        >
                          <span className="text-sm font-semibold text-foreground-800 pr-4">
                            {isDe ? faq.question_de : faq.question_en}
                          </span>
                          <i className={`${openFaq === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 flex-shrink-0 text-lg`} />
                        </button>
                        {openFaq === i && (
                          <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                            {isDe ? faq.answer_de : faq.answer_en}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Wichtige Informationen */}
              <ImportantInfo />

            </div>

            {/* Sidebar - Booking Form */}
            <div className="w-full lg:w-[400px] flex-shrink-0">
              <div className="lg:sticky lg:top-36 bg-background-50 border border-background-200 rounded-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                {/* Price */}
                <div className="mb-6 pb-5 border-b border-background-200">
                  {tour.pricing_tiers && tour.pricing_tiers.length > 1 ? (
                    <div>
                      <p className="text-xs font-bold text-foreground-800 uppercase tracking-wider mb-4">{isDe ? 'Preistabelle' : 'Price Table'}</p>
                      <div className="space-y-2">
                        {tour.pricing_tiers.map((tier) => (
                          <div key={tier.persons} className="flex items-center justify-between text-sm bg-background-100 px-3 py-2 rounded-sm">
                            <span className="text-foreground-700 font-medium">
  {isDe ? tier.persons : tier.persons.replace('Pers.', 'Persons')}
</span>
                            <span className="font-bold text-foreground-900">
                              {getCurrencySymbol()}{getConvertedPrice(tier.price)}<span className="text-xs text-foreground-500 font-normal">
  {isDe ? 'pro Person' : 'per person'}
</span>
                            </span>
                          </div>
                        ))}
                        {tour.child_price != null && (
                          <div className="flex items-center justify-between text-sm bg-accent-50 px-3 py-2 rounded-sm mt-1">
                            <span className="text-foreground-700 font-medium">{isDe ? 'Kind' : 'Child'}</span>
                            <span className="font-bold text-foreground-900">
                              {getCurrencySymbol()}{getConvertedPrice(tour.child_price)} <span className="text-xs text-foreground-500 font-normal">/ {isDe ? 'Kind' : 'Child'}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-3xl font-bold text-accent-600">
                          {getCurrencySymbol()}{getConvertedPrice(tour.price)}
                        </span>
                        <span className="text-sm text-foreground-500">pro Person</span>
                      </div>
                      {tour.child_price != null && (
                        <p className="text-xs text-foreground-500 mt-1.5">
                          Kind: {getCurrencySymbol()}{getConvertedPrice(tour.child_price)}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {formSent ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <i className="ri-checkbox-circle-fill text-accent-500 text-4xl" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground-800 mb-2">{isDe ? 'Anfrage gesendet!' : 'Booking Request Sent!'}</h3>
                    <p className="text-sm text-foreground-600"> {isDe
        ? 'Wir werden uns innerhalb von 24 Stunden bei Ihnen melden.'
        : 'We will contact you within 24 hours.'}</p>
                  </div>
                ) : (
                  <form data-readdy-form="" onSubmit={handleBookingSubmit}>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5"> {isDe ? 'Buchungsanfrage' : 'Booking Request'}</h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-foreground-700 mb-1.5">{isDe ? 'Reisedatum' : 'Travel Date'}</label>
                        <input type="date" name="date" value={bookingForm.date} onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })} required className="w-full px-3.5 py-3 border border-background-300 rounded-sm text-sm text-foreground-800 focus:outline-none focus:border-accent-500 bg-background-50 transition-colors" />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div>
  <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
    {isDe ? 'Erwachsene' : 'Adults'}
  </label>

  <div className="flex items-center w-full border border-background-300 rounded-sm overflow-hidden focus-within:ring-2 focus-within:ring-accent-400">
    <button
      type="button"
      onClick={() =>
  setBookingForm({
    ...bookingForm,
    adults: Math.max(2, bookingForm.adults - 1),
  })
}
      className="w-12 h-12 flex items-center justify-center text-xl font-bold text-foreground-700 hover:bg-background-100 active:bg-background-200"
      aria-label={isDe ? 'Erwachsene reduzieren' : 'Decrease adults'}
    >
      −
    </button>

    <input
  type="number"
  name="adults"
  min={2}
  max={20}
  value={bookingForm.adults}
  onFocus={(e) => e.currentTarget.select()}
  onChange={(e) =>
    setBookingForm({
      ...bookingForm,
      adults: Math.min(20, Math.max(2, parseInt(e.target.value) || 2)),
    })
  }
  required
  className="w-full h-12 text-center border-0 focus:outline-none focus:ring-0 appearance-none"
/>
    <button
      type="button"
      onClick={() =>
        setBookingForm({
          ...bookingForm,
          adults: Math.min(20, bookingForm.adults + 1),
        })
      }
      className="w-12 h-12 flex items-center justify-center text-xl font-bold text-foreground-700 hover:bg-background-100 active:bg-background-200"
      aria-label={isDe ? 'Erwachsene erhöhen' : 'Increase adults'}
    >
      +
    </button>
  </div>
</div>
                        </div>
                        <div>
                          <div>
  <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
    {isDe ? 'Kinder' : 'Children'}
  </label>

  <div className="flex items-center w-full border border-background-300 rounded-sm overflow-hidden focus-within:ring-2 focus-within:ring-accent-400">
    <button
      type="button"
      onClick={() =>
        setBookingForm({
          ...bookingForm,
          children: Math.max(0, bookingForm.children - 1),
        })
      }
      className="w-12 h-12 flex items-center justify-center text-xl font-bold text-foreground-700 hover:bg-background-100 active:bg-background-200"
      aria-label={isDe ? 'Kinder reduzieren' : 'Decrease children'}
    >
      −
    </button>

    <input
      type="number"
      name="children"
      min={0}
      max={10}
      value={bookingForm.children}
      onChange={(e) =>
        setBookingForm({
          ...bookingForm,
          children: Math.min(10, Math.max(0, parseInt(e.target.value) || 0)),
        })
      }
      className="w-full h-12 text-center border-0 focus:outline-none focus:ring-0 appearance-none"
    />

    <button
      type="button"
      onClick={() =>
        setBookingForm({
          ...bookingForm,
          children: Math.min(10, bookingForm.children + 1),
        })
      }
      className="w-12 h-12 flex items-center justify-center text-xl font-bold text-foreground-700 hover:bg-background-100 active:bg-background-200"
      aria-label={isDe ? 'Kinder erhöhen' : 'Increase children'}
    >
      +
    </button>
  </div>
</div>
                        </div>
                      </div>

                      {/* Total */}
                      <div className="bg-primary-800 rounded-sm p-4 flex items-center justify-between">
                        <span className="text-sm font-medium text-background-200">{isDe ? 'Gesamtpreis' : 'Total Price'}</span>
                        <span className="text-xl font-bold text-accent-400">
                          {getCurrencySymbol()}{totalPrice}
                        </span>
                      </div>

                      <div className="border-t border-background-200 pt-4">
                        <p className="text-xs font-bold text-foreground-800 uppercase tracking-wider mb-4">{isDe ? 'Kundendaten' : 'Customer Details'}</p>
                        <div className="space-y-3.5">
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1">{isDe ? 'Vollständiger Name' : 'Full Name'}</label>
                            <input type="text" name="name" value={bookingForm.name} onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })} required placeholder={isDe ? 'Max Mustermann' : 'Max Mustermann'} className="w-full px-3.5 py-3 border border-background-300 rounded-sm text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 bg-background-50 transition-colors" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1">{isDe ? 'E-Mail-Adresse' : 'Email Address'}</label>
                            <input type="email" name="email" value={bookingForm.email} onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })} required placeholder={isDe ? 'Ihre E-Mail-Adresse' : 'Your email address'} className="w-full px-3.5 py-3 border border-background-300 rounded-sm text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 bg-background-50 transition-colors" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1">{isDe ? 'WhatsApp-Nummer' : 'WhatsApp Number'}</label>
                            <input type="tel" name="phone" value={bookingForm.phone} onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })} required placeholder={isDe ? 'Ihre WhatsApp-Nummer' : 'Your WhatsApp number'} className="w-full px-3.5 py-3 border border-background-300 rounded-sm text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 bg-background-50 transition-colors" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1">{isDe ? 'Hotelname' : 'Hotel Name'}</label>
                            <input type="text" name="hotel" value={bookingForm.hotel} onChange={(e) => setBookingForm({ ...bookingForm, hotel: e.target.value })} required placeholder={isDe ? 'z.B. Steigenberger' : 'e.g. Steigenberger'} className="w-full px-3.5 py-3 border border-background-300 rounded-sm text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 bg-background-50 transition-colors" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1">{isDe ? 'Besondere Wünsche' : 'Special Requests'}</label>
                            <textarea name="notes" value={bookingForm.notes} onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })} placeholder={isDe ? 'z.B. Allergien, besondere Wünsche...' : 'e.g. allergies, special requests...'} maxLength={500} rows={3} className="w-full px-3.5 py-3 border border-background-300 rounded-sm text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 bg-background-50 resize-none transition-colors" />
                          </div>
                        </div>
                      </div>

                      <button type="submit" className="w-full py-4 bg-accent-500 text-primary-950 text-base font-bold rounded-sm hover:bg-accent-400 transition-all cursor-pointer uppercase tracking-wider whitespace-nowrap shadow-[0_4px_14px_rgba(200,160,80,0.35)] hover:shadow-[0_6px_20px_rgba(200,160,80,0.45)]">
                        {isDe ? 'JETZT UNVERBINDLICH ANFRAGEN' : 'SEND BOOKING REQUEST'}
                      </button>

                      <p className="text-xs text-foreground-400 text-center leading-relaxed">
                        {isDe
  ? 'Keine Online-Zahlung erforderlich. Sie bezahlen direkt vor Ort.'
  : 'No online payment required. You pay directly on the day of the tour.'
}
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {lightboxOpen && (
  <div
    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
    onClick={() => setLightboxOpen(false)}
  >
    <button
      className="absolute top-6 right-6 text-white text-5xl"
      onClick={() => setLightboxOpen(false)}
    >
      ×
      
      <button
  className="absolute left-6 text-white text-5xl hover:text-accent-400 transition"
  onClick={(e) => {
    e.stopPropagation();
    setActiveImage(
      activeImage === 0 ? allImages.length - 1 : activeImage - 1
    );
  }}
>
  ❮
</button>
    </button>

    <img
    
      src={allImages[activeImage] || tour.image_url}
      alt={tour.title_de}
      className="max-w-[95vw] max-h-[90vh] object-contain"
      onClick={(e) => e.stopPropagation()}
    />
    <button
  className="absolute right-6 text-white text-5xl hover:text-accent-400 transition"
  onClick={(e) => {
    e.stopPropagation();
    setActiveImage(
      activeImage === allImages.length - 1 ? 0 : activeImage + 1
    );
  }}
>
  ❯
</button>
  </div>
)}
      <Footer />
    </div>
  );
}